import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { Button } from "./atoms";
import { ConfirmDeleteModal } from "./molecules/ConfirmDeleteModal";
import type { OrganizationSelection } from "./molecules/OrganizationSelectPopup/OrganizationSelectPopup";
import { deleteStore } from "../api/stores";

/**
 * Step 間で引き回す店舗フォーム state（新規・編集共通）。
 * organization / departmentIds はバックエンド schema 確定までのプレースホルダ。
 * organization にはホーム画面と同じ StoreSwitchPopup の選択結果（org levels）を保持する。
 */
export interface StoreFormState {
    code: string;
    name: string;
    shortName: string;
    /** 稼働状況 (1=稼働前, 2=稼働中, 3=閉店, 4=その他) */
    status: number;
    organization: OrganizationSelection | null;
    departmentIds: number[];
}

/** 店舗ステータスの表示順とラベル */
export const STORE_STATUS_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: "稼働前" },
    { value: 2, label: "稼働中" },
    { value: 3, label: "閉店" },
    { value: 4, label: "その他" },
];

/** デフォルトステータス（新規登録時） */
export const DEFAULT_STORE_STATUS = 2;

export interface StoreFormOutletContext {
    setPageError: (message: string | null) => void;
    invalidFields: string[];
    setInvalidFields: (fields: string[]) => void;
    pendingFormState: StoreFormState | null;
    setPendingFormState: (state: StoreFormState | null) => void;
}

/** 「基本情報」タブに含まれる必須フィールドのキー一覧 */
const BASIC_INFO_REQUIRED_FIELDS = ["code", "name"] as const;

/** 店舗コードの最大桁数 */
export const STORE_CODE_MAX_LENGTH = 50;
/** 店舗名の最大文字数 */
export const STORE_NAME_MAX_LENGTH = 50;
/** 略称の最大文字数 */
export const STORE_SHORT_NAME_MAX_LENGTH = 10;

const STORE_CODE_PATTERN = /^[A-Za-z0-9]+$/;

/**
 * 店舗コードの書式チェック（必須チェックは別途）
 * @returns エラーメッセージ。問題なければ null。
 */
export const validateStoreCode = (code: string): string | null => {
    if (code.length === 0) return null;
    if (code.length > STORE_CODE_MAX_LENGTH) {
        return `店舗コードは${STORE_CODE_MAX_LENGTH}桁以内で入力してください`;
    }
    if (!STORE_CODE_PATTERN.test(code)) {
        return "店舗コードは半角英数字で入力してください";
    }
    return null;
};

/**
 * 店舗名の文字数チェック（必須チェックは別途）
 */
export const validateStoreName = (name: string): string | null => {
    if (name.length === 0) return null;
    if (name.length > STORE_NAME_MAX_LENGTH) {
        return `${STORE_NAME_MAX_LENGTH}文字以内`;
    }
    return null;
};

/**
 * 略称の文字数チェック
 */
export const validateStoreShortName = (shortName: string): string | null => {
    if (shortName.length === 0) return null;
    if (shortName.length > STORE_SHORT_NAME_MAX_LENGTH) {
        return `${STORE_SHORT_NAME_MAX_LENGTH}文字以内`;
    }
    return null;
};

interface MenuItem {
    id: string;
    label: string;
    path: string;
    displayLabel: string;
}

const StoreEditLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;

    const [pageError, setPageError] = useState<string | null>(null);
    const [invalidFields, setInvalidFields] = useState<string[]>([]);
    const [pendingFormState, setPendingFormState] = useState<StoreFormState | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // 画面遷移時に子ページエラーを自動リセット
    useEffect(() => {
        setPageError(null);
    }, [location.pathname]);

    // 対象店舗が変わったら state をリセット
    useEffect(() => {
        setPendingFormState(null);
        setInvalidFields([]);
    }, [id, isEdit]);

    const menuItems = useMemo<MenuItem[]>(() => {
        const base = isEdit ? `/org-settings/store/${id}` : "/org-settings/store/new";
        const basicPath = isEdit ? `${base}/edit` : base;
        return [
            { id: "1", label: "基本情報", path: basicPath, displayLabel: "1. 基本情報" },
            { id: "2", label: "組織", path: `${base}/organization`, displayLabel: "2. 組織" },
        ];
    }, [id, isEdit]);

    const activeMenuId = useMemo(() => {
        if (location.pathname.endsWith("/organization")) return "2";
        return "1";
    }, [location]);

    // 削除（バックエンド未接続: 一覧へ戻るスタブ。schema 確定後に deleteStore に接続）
    const handleDelete = async () => {
        if (!id) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            await deleteStore(parseInt(id, 10));
            navigate("/org-settings/store");
        } catch (err) {
            console.error("Failed to delete store:", err);
            setDeleteError(err instanceof Error ? err.message : "削除に失敗しました");
            setDeleting(false);
        }
    };

    // タブの赤表示: invalidFields に含まれ、かつ pendingFormState でも未入力の場合に true
    const hasBasicInfoError = useMemo(() => {
        const basicKeys = BASIC_INFO_REQUIRED_FIELDS as readonly string[];
        return invalidFields.some((f) => {
            if (!basicKeys.includes(f)) return false;
            if (!pendingFormState) return true;
            const value = pendingFormState[f as keyof StoreFormState];
            return !value || (typeof value === "string" && !value.trim());
        });
    }, [invalidFields, pendingFormState]);

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* ヘッダー */}
            <div className="px-6 py-4 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-1 h-6 bg-brand-500 rounded-full" />
                    <h1 className="text-xl font-bold text-gray-800">
                        店舗の登録・編集
                    </h1>
                </div>
                {(deleteError || pageError) && (
                    <div className="mt-2 text-sm text-red-600">{deleteError ?? pageError}</div>
                )}
            </div>

            {/* フォームエリア */}
            <div className="flex-1 overflow-hidden px-6 pb-6">
                <div className="flex h-full gap-4">
                    {/* サイドメニュー */}
                    <div className="w-44 bg-white rounded-lg py-4 flex-shrink-0 flex flex-col">
                        <nav className="space-y-1 flex-1">
                            {menuItems.map((item) => {
                                const hasError = item.id === "1" && hasBasicInfoError;
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => navigate(item.path)}
                                        className={clsx(
                                            "w-full text-left px-4 py-2 text-sm",
                                            hasError
                                                ? "text-red-600 font-medium"
                                                : activeMenuId === item.id
                                                    ? "text-brand-500 font-medium"
                                                    : "text-gray-600 hover:text-gray-900"
                                        )}
                                    >
                                        {item.displayLabel}
                                    </button>
                                );
                            })}
                        </nav>
                        {isEdit && (
                            <div className="px-4 pt-4 mt-auto">
                                <Button
                                    variant="danger"
                                    size="md"
                                    onClick={() => setShowDeleteModal(true)}
                                    disabled={deleting}
                                    className="w-full"
                                >
                                    削除
                                </Button>
                            </div>
                        )}
                    </div>

                    <ConfirmDeleteModal
                        isOpen={showDeleteModal}
                        onCancel={() => setShowDeleteModal(false)}
                        onConfirm={() => {
                            setShowDeleteModal(false);
                            handleDelete();
                        }}
                        isDeleting={deleting}
                        title="店舗の削除"
                        message={
                            pendingFormState?.name
                                ? `店舗「${pendingFormState.name}」を削除します。紐づく組織情報・使用部門の登録も合わせて削除されます。よろしいですか？`
                                : "この店舗を削除します。紐づく組織情報・使用部門の登録も合わせて削除されます。よろしいですか？"
                        }
                    />

                    {/* タブごとの内容 */}
                    <Outlet context={{
                        setPageError,
                        invalidFields,
                        setInvalidFields,
                        pendingFormState,
                        setPendingFormState,
                    } satisfies StoreFormOutletContext} />
                </div>
            </div>
        </div>
    );
};

export default StoreEditLayout;
